import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

function ListPanel({ type }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", id_number: "", phone: "", reason: "" });

  const load = async () => {
    const { data } = await api.get(`/${type}`);
    setEntries(data);
  };

  useEffect(() => {
    load();
  }, [type]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post(`/${type}`, form);
    toast.success(`Added to ${type}`);
    setForm({ name: "", id_number: "", phone: "", reason: "" });
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/${type}/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid={`${type}-panel`}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid={`add-${type}-button`} className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm gap-2">
            <Plus size={16} /> Add Entry
          </Button>
        </DialogTrigger>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="uppercase">Add to {type}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input
              data-testid={`${type}-name-input`}
              placeholder="Full name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-sm bg-white border-slate-300"
            />
            <Input
              data-testid={`${type}-id-input`}
              placeholder="ID number (optional)"
              value={form.id_number}
              onChange={(e) => setForm({ ...form, id_number: e.target.value })}
              className="rounded-sm bg-white border-slate-300"
            />
            <Input
              data-testid={`${type}-phone-input`}
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-sm bg-white border-slate-300"
            />
            <Textarea
              data-testid={`${type}-reason-input`}
              placeholder="Reason"
              required
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="rounded-sm bg-white border-slate-300"
            />
            <Button type="submit" data-testid={`${type}-submit-button`} className="w-full rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
              Save
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <div className="border border-slate-200 bg-white">
        {entries.map((e) => (
          <div key={e.id} data-testid={`${type}-entry-${e.id}`} className="flex items-center justify-between p-4 border-b border-slate-100">
            <div>
              <p className="font-bold text-brand-navy">{e.name}</p>
              <p className="text-sm text-slate-500">{e.reason}</p>
              <p className="text-xs text-slate-400 mt-1">
                Added by {e.added_by_name} · {new Date(e.created_at).toLocaleDateString()}
              </p>
            </div>
            <button data-testid={`delete-${type}-${e.id}`} onClick={() => remove(e.id)} className="text-slate-400 hover:text-red-600">
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        {!entries.length && <p className="p-6 text-center text-slate-400">No entries yet</p>}
      </div>
    </div>
  );
}

export default function Lists() {
  return (
    <div className="p-8 space-y-6" data-testid="lists-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Compliance</span>
        <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Watchlist &amp; Blacklist</h1>
      </div>
      <Tabs defaultValue="watchlist">
        <TabsList className="bg-white border border-slate-200 rounded-sm">
          <TabsTrigger value="watchlist" data-testid="tab-watchlist" className="rounded-sm">
            Watchlist
          </TabsTrigger>
          <TabsTrigger value="blacklist" data-testid="tab-blacklist" className="rounded-sm">
            Blacklist
          </TabsTrigger>
        </TabsList>
        <TabsContent value="watchlist">
          <ListPanel type="watchlist" />
        </TabsContent>
        <TabsContent value="blacklist">
          <ListPanel type="blacklist" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
